import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useSenders } from '@/hooks/useSenders';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { AlertCircle, Check, Pencil, X, Users, UserPlus, Wifi } from 'lucide-react';
import { IntegrationHealthCard } from '@/components/settings/IntegrationHealthCard';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { toSafeUiError } from '@/lib/user-safe-error';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface TeamMember {
  userId: string;
  email: string | null;
  fullName: string | null;
  role: string;
  senders: Array<{ id: string; displayName: string; isActive: boolean }>;
  joinedAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sender Management (existing - unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const SenderManagementSkeleton = () => (
  <div className="space-y-3" role="status" aria-label="Loading">
    {[1, 2].map((item) => (
      <div key={item} className="flex items-center gap-4 rounded-lg border p-3">
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

const SenderManagement = () => {
  const {
    data: senders,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useSenders({ includeInactive: true });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const previousEditingIdRef = useRef<string | null>(null);
  const senderUpdateInFlightRef = useRef(false);

  useEffect(() => {
    const previousEditingId = previousEditingIdRef.current;
    if (editingId === null && previousEditingId) {
      editButtonRefs.current[previousEditingId]?.focus();
    }
    previousEditingIdRef.current = editingId;
  }, [editingId]);

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<{ display_name: string; is_active: boolean; ai_drafts_enabled: boolean }>;
    }) => {
      const { error } = await supabase
        .from('os_heyreach_senders')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['heyreach-senders'] });
      queryClient.invalidateQueries({ queryKey: ['heyreach-senders-with-counts'] });
      setEditingId(null);
      toast({ title: 'Saved', description: 'Sender updated successfully.' });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update sender.',
        variant: 'destructive',
      });
    },
  });

  const startEdit = (id: string, displayName: string): void => {
    if (senderUpdateInFlightRef.current) return;
    setEditingId(id);
    setEditValue(displayName);
  };

  const submitSenderUpdate = (
    id: string,
    updates: Partial<{
      display_name: string;
      is_active: boolean;
      ai_drafts_enabled: boolean;
    }>,
  ): void => {
    if (senderUpdateInFlightRef.current) return;
    senderUpdateInFlightRef.current = true;
    try {
      updateMutation.mutate(
        { id, updates },
        {
          onSettled: () => {
            senderUpdateInFlightRef.current = false;
          },
        },
      );
    } catch (mutationError) {
      senderUpdateInFlightRef.current = false;
      throw mutationError;
    }
  };

  const saveEdit = (id: string): void => {
    const value = editValue.trim();
    if (!value) return;
    submitSenderUpdate(id, { display_name: value });
  };

  const cancelEdit = (): void => {
    if (senderUpdateInFlightRef.current) return;
    setEditingId(null);
  };

  const toggleActive = (id: string, currentValue: boolean): void => {
    submitSenderUpdate(id, { is_active: !currentValue });
  };

  const toggleAiDrafts = (id: string, currentValue: boolean | null): void => {
    submitSenderUpdate(id, {
      ai_drafts_enabled: !(currentValue ?? true),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wifi className="h-5 w-5" aria-hidden="true" />
          LinkedIn Seats
        </CardTitle>
        <CardDescription>
          Manage display names for your HeyReach sender accounts. These names appear in the
          inbox filter dropdown. Toggle AI drafts per seat.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SenderManagementSkeleton />
        ) : error ? (
          <SafeQueryAlert
            error={error}
            isRetrying={isFetching}
            onRetry={() => {
              void refetch();
            }}
            title="LinkedIn seats are unavailable"
          />
        ) : senders?.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No senders yet. Senders are automatically created when HeyReach webhooks are received.
          </p>
        ) : (
          <div className="space-y-4">
            {senders?.map((sender) => (
              <div
                key={sender.id}
                className="flex flex-col items-stretch gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:gap-4"
              >
                {editingId === sender.id ? (
                  <>
                    <div className="min-w-0 flex-1">
                      <Label htmlFor={`sender-name-${sender.id}`} className="sr-only">
                        Display name for{' '}
                        <span data-content-origin="external">
                          {sender.display_name}
                        </span>
                      </Label>
                      <Input
                        id={`sender-name-${sender.id}`}
                        className="min-h-11"
                        value={editValue}
                        onChange={(event) => setEditValue(event.target.value)}
                        disabled={updateMutation.isPending}
                        aria-invalid={!editValue.trim()}
                        aria-describedby={
                          editValue.trim() ? undefined : `sender-name-help-${sender.id}`
                        }
                        autoFocus
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') saveEdit(sender.id);
                          if (event.key === 'Escape') cancelEdit();
                        }}
                      />
                      {!editValue.trim() && (
                        <p
                          id={`sender-name-help-${sender.id}`}
                          className="mt-1 text-xs text-destructive"
                          role="alert"
                        >
                          Display name is required.
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                        onClick={() => saveEdit(sender.id)}
                        disabled={updateMutation.isPending || !editValue.trim()}
                        aria-labelledby={`save-sender-name-label-${sender.id} save-sender-name-value-${sender.id}`}
                      >
                        <span
                          id={`save-sender-name-label-${sender.id}`}
                          className="sr-only"
                        >
                          Save display name for
                        </span>
                        <span
                          id={`save-sender-name-value-${sender.id}`}
                          className="sr-only"
                          data-content-origin="external"
                        >
                          {sender.display_name}
                        </span>
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                        onClick={cancelEdit}
                        disabled={updateMutation.isPending}
                        aria-labelledby={`cancel-sender-name-label-${sender.id} cancel-sender-name-value-${sender.id}`}
                      >
                        <span
                          id={`cancel-sender-name-label-${sender.id}`}
                          className="sr-only"
                        >
                          Cancel editing
                        </span>
                        <span
                          id={`cancel-sender-name-value-${sender.id}`}
                          className="sr-only"
                          data-content-origin="external"
                        >
                          {sender.display_name}
                        </span>
                        <X className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="min-w-0 flex-1">
                      <p
                        id={`sender-display-name-${sender.id}`}
                        className="font-medium"
                        data-content-origin="external"
                      >
                        {sender.display_name}
                      </p>
                      {sender.email && (
                        <p
                          className="truncate text-sm text-muted-foreground"
                          data-content-origin="external"
                        >
                          {sender.email}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
                      <Button
                        ref={(node) => {
                          editButtonRefs.current[sender.id] = node;
                        }}
                        size="sm"
                        variant="ghost"
                        className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
                        onClick={() => startEdit(sender.id, sender.display_name)}
                        disabled={updateMutation.isPending}
                        aria-labelledby={`edit-sender-name-label-${sender.id} sender-display-name-${sender.id}`}
                      >
                        <span
                          id={`edit-sender-name-label-${sender.id}`}
                          className="sr-only"
                        >
                          Edit display name for
                        </span>
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`sender-active-${sender.id}`}
                          className="relative h-11 min-h-11 w-11 min-w-11 bg-transparent before:absolute before:left-0 before:top-1/2 before:h-6 before:w-11 before:-translate-y-1/2 before:rounded-full before:bg-input before:transition-colors before:content-[''] data-[state=checked]:bg-transparent data-[state=checked]:before:bg-primary data-[state=unchecked]:bg-transparent [&>span]:relative [&>span]:z-10"
                          checked={sender.is_active}
                          onCheckedChange={() => toggleActive(sender.id, sender.is_active)}
                          disabled={updateMutation.isPending}
                          aria-describedby={`sender-active-status-${sender.id}`}
                        />
                        <Label
                          htmlFor={`sender-active-${sender.id}`}
                          className="text-sm text-muted-foreground"
                        >
                          Seat active
                        </Label>
                        <span id={`sender-active-status-${sender.id}`} className="sr-only">
                          {sender.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`sender-ai-drafts-${sender.id}`}
                          className="relative h-11 min-h-11 w-11 min-w-11 bg-transparent before:absolute before:left-0 before:top-1/2 before:h-6 before:w-11 before:-translate-y-1/2 before:rounded-full before:bg-input before:transition-colors before:content-[''] data-[state=checked]:bg-transparent data-[state=checked]:before:bg-primary data-[state=unchecked]:bg-transparent [&>span]:relative [&>span]:z-10"
                          checked={sender.ai_drafts_enabled ?? true}
                          onCheckedChange={() =>
                            toggleAiDrafts(sender.id, sender.ai_drafts_enabled)
                          }
                          disabled={updateMutation.isPending}
                          aria-describedby={`sender-ai-drafts-status-${sender.id}`}
                        />
                        <Label
                          htmlFor={`sender-ai-drafts-${sender.id}`}
                          className="text-sm text-muted-foreground"
                        >
                          AI drafts
                        </Label>
                        <span id={`sender-ai-drafts-status-${sender.id}`} className="sr-only">
                          {sender.ai_drafts_enabled ?? true ? 'AI on' : 'AI off'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Team Management (NEW - admin only)
// ─────────────────────────────────────────────────────────────────────────────

const useTeamMembers = () => {
  const { session } = useAuth();
  return useQuery<TeamMember[]>({
    queryKey: ['team-members'],
    queryFn: async () => {
      const response = await fetch('/api/admin/team', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch team');
      const json = await response.json();
      return json.members || [];
    },
    staleTime: 30000,
  });
};

const roleBadgeVariant = (role: string): 'default' | 'secondary' | 'outline' => {
  if (role === 'owner') return 'default';
  if (role === 'admin') return 'secondary';
  return 'outline';
};

const isValidInviteEmail = (value: string): boolean => {
  const normalized = value.trim();
  return (
    normalized.length <= 320
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(normalized)
  );
};

const TeamManagement = () => {
  const {
    data: members,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useTeamMembers();
  const { toast } = useToast();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteAttempted, setInviteAttempted] = useState(false);
  const inviteButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousInviteOpenRef = useRef(false);
  const inviteInFlightRef = useRef(false);
  const inviteNameIsValid = Boolean(inviteName.trim());
  const inviteEmailIsValid = isValidInviteEmail(inviteEmail);

  useEffect(() => {
    if (!showInvite && previousInviteOpenRef.current) {
      inviteButtonRef.current?.focus();
    }
    previousInviteOpenRef.current = showInvite;
  }, [showInvite]);

  const closeInvite = (): void => {
    if (inviteInFlightRef.current) return;
    setShowInvite(false);
    setInviteAttempted(false);
  };

  const inviteMutation = useMutation({
    mutationFn: async ({ email, fullName }: { email: string; fullName: string }) => {
      const response = await fetch('/api/admin/invite-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ email, fullName, role: 'member' }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error?.message || 'Failed to invite user');
      }
      return response.json();
    },
    onSuccess: () => {
      inviteInFlightRef.current = false;
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      closeInvite();
      setInviteEmail('');
      setInviteName('');
      toast({ title: 'Invited', description: 'User invited successfully.' });
    },
    onError: (mutationError: Error) => {
      inviteInFlightRef.current = false;
      const safeError = toSafeUiError(mutationError);
      toast({
        title: safeError.title,
        description: `${safeError.description} ${safeError.recovery}`,
        variant: 'destructive',
      });
    },
  });

  const handleInvite = (): void => {
    setInviteAttempted(true);
    if (!inviteEmailIsValid || !inviteNameIsValid) return;
    if (inviteInFlightRef.current) return;
    inviteInFlightRef.current = true;
    try {
      inviteMutation.mutate(
        { email: inviteEmail.trim(), fullName: inviteName.trim() },
        {
          onSettled: () => {
            inviteInFlightRef.current = false;
          },
        },
      );
    } catch (mutationError) {
      inviteInFlightRef.current = false;
      throw mutationError;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" aria-hidden="true" />
              Team
            </CardTitle>
            <CardDescription>
              Manage team members and their LinkedIn seat assignments.
            </CardDescription>
          </div>
          <Button
            ref={inviteButtonRef}
            size="sm"
            variant="outline"
            className="min-h-11 w-full md:min-h-11 sm:w-auto"
            onClick={() => {
              if (showInvite) {
                closeInvite();
              } else {
                setShowInvite(true);
              }
            }}
            disabled={inviteMutation.isPending}
            aria-expanded={showInvite}
            aria-controls="team-invite-panel"
          >
            <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            {showInvite ? 'Close invite form' : 'Invite team member'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showInvite && (
          <div
            id="team-invite-panel"
            className="mb-4 space-y-3 rounded-lg border border-dashed p-4"
            aria-busy={inviteMutation.isPending}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="invite-full-name" className="text-xs text-muted-foreground">
                  Full name
                </Label>
                <Input
                  id="invite-full-name"
                  className="min-h-11"
                  placeholder="Stefan Stojkovic"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  disabled={inviteMutation.isPending}
                  aria-required="true"
                  aria-invalid={inviteAttempted && !inviteNameIsValid}
                  aria-describedby={
                    inviteAttempted && !inviteNameIsValid
                      ? 'invite-full-name-error'
                      : undefined
                  }
                  autoFocus
                />
                {inviteAttempted && !inviteNameIsValid && (
                  <p id="invite-full-name-error" className="mt-1 text-xs text-destructive">
                    Full name is required.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="invite-email" className="text-xs text-muted-foreground">
                  Email
                </Label>
                <Input
                  id="invite-email"
                  className="min-h-11"
                  type="email"
                  placeholder="stefan@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  disabled={inviteMutation.isPending}
                  maxLength={320}
                  aria-required="true"
                  aria-invalid={inviteAttempted && !inviteEmailIsValid}
                  aria-describedby={
                    inviteAttempted && !inviteEmailIsValid
                      ? 'invite-email-error'
                      : undefined
                  }
                />
                {inviteAttempted && !inviteEmailIsValid && (
                  <p id="invite-email-error" className="mt-1 text-xs text-destructive">
                    Enter a valid email address.
                  </p>
                )}
              </div>
            </div>
            {(
              inviteAttempted
              && (!inviteEmailIsValid || !inviteNameIsValid)
            ) && (
              <p className="text-sm text-destructive" role="alert">
                Enter a full name and a valid email address before sending the invitation.
              </p>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="min-h-11 md:min-h-11"
                onClick={closeInvite}
                disabled={inviteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="min-h-11 md:min-h-11"
                onClick={handleInvite}
                disabled={inviteMutation.isPending}
              >
                {inviteMutation.isPending ? 'Inviting...' : 'Send Invite'}
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <SenderManagementSkeleton />
        ) : error ? (
          <SafeQueryAlert
            error={error}
            isRetrying={isFetching}
            onRetry={() => {
              void refetch();
            }}
            title="Team members are unavailable"
          />
        ) : !members?.length ? (
          <p className="text-sm text-muted-foreground">No team members yet.</p>
        ) : (
          <div className="space-y-3">
            {members.map((member) => (
              <div
                key={member.userId}
                className="flex flex-col items-start gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:gap-4"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                  data-content-origin="user"
                >
                  {String(member.fullName || member.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium" data-content-origin="user">
                      {member.fullName || member.email || 'Unknown'}
                    </p>
                    <Badge variant={roleBadgeVariant(member.role)} className="text-xs">
                      {member.role}
                    </Badge>
                  </div>
                  {member.email && (
                    <p
                      className="truncate text-xs text-muted-foreground"
                      data-content-origin="user"
                    >
                      {member.email}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 sm:justify-end">
                  {member.senders.length > 0 ? (
                    member.senders.map((s) => (
                      <Badge
                        key={s.id}
                        variant={s.isActive ? 'secondary' : 'outline'}
                        className="text-xs"
                      >
                        <span data-content-origin="external">{s.displayName}</span>
                      </Badge>
                    ))
                  ) : (
                    <span className="text-xs text-muted-foreground">No seat mapped</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface SafeQueryAlertProps {
  error: unknown;
  isRetrying: boolean;
  onRetry: () => void;
  title: string;
}

function SafeQueryAlert({
  error,
  isRetrying,
  onRetry,
  title,
}: SafeQueryAlertProps): React.ReactElement {
  const safeError = toSafeUiError(error);

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          {safeError.description} {safeError.recovery}
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-11 md:min-h-11"
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? 'Retrying...' : 'Retry'}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings Page
// ─────────────────────────────────────────────────────────────────────────────

export default function Settings(): React.ReactElement {
  useDocumentTitle('Settings — Tacit CRM');
  const { isAdmin } = useAuth();

  return (
    <Layout>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="border-b bg-card p-4">
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground">
            Manage LinkedIn seats, team, and integration preferences.
          </p>
        </div>
        <div className="min-w-0 flex-1 overflow-x-clip p-4">
          <div className="mx-auto max-w-3xl">
            <div className="space-y-4">
              {isAdmin && <TeamManagement />}
              <IntegrationHealthCard />
              {isAdmin && <SenderManagement />}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
